import { useSelector } from "react-redux";
import { Button } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { formatDateTimeRu } from "../utils/formatDate";
import { resolveAnalyticsView } from "../utils/analyticsDemoData";
import { resolveAnalyticsCharts } from "../utils/analyticsCharts";
import { setAuthToken } from "../utils/tokenHeaders";
import AnalyticsCharts from "./analytics/AnalyticsCharts";
import { isTrainerGame } from "../utils/contentType";

const prodURL = "/api";
const apiUrl =
  window.location.hostname === "localhost"
    ? "http://localhost:3004/api"
    : prodURL;

const SCENARIO_DETAIL_COLUMNS = [
  "Пользователь",
  "Запуск",
  "Завершение",
  "Дошёл до финала",
  "Ошибки",
  "Маршрут",
];

const TRAINER_DETAIL_COLUMNS = [
  "Пользователь",
  "Запуск",
  "Завершение",
  "Завершил",
  "Ответов",
  "Правильных",
  "Точность",
  "Маршрут",
];

export default function ScenarioAnalytics({ embedded = false }) {
  const game = useSelector((state) => state.user.game);
  const games = useSelector((state) => state.user.games);
  const isTrainer = isTrainerGame(game);
  const detailColumns = isTrainer ? TRAINER_DETAIL_COLUMNS : SCENARIO_DETAIL_COLUMNS;
  const { analytics, sessions } = resolveAnalyticsView(game);
  const charts = resolveAnalyticsCharts(
    game?._id,
    games,
    sessions,
    analytics,
    false
  );

  const summary = isTrainer
    ? [
        { label: "Запусков", value: analytics.starts ?? 0 },
        { label: "Завершений", value: analytics.completions ?? 0 },
        { label: "Уникальных участников", value: analytics.uniquePlayers ?? 0 },
        { label: "Всего ответов", value: analytics.trainerAnswers ?? 0 },
        { label: "Правильных ответов", value: analytics.trainerCorrect ?? 0 },
      ]
    : [
        { label: "Запусков", value: analytics.starts ?? 0 },
        { label: "Завершений", value: analytics.completions ?? 0 },
        { label: "Уникальных сотрудников", value: analytics.uniquePlayers ?? 0 },
        { label: "Ошибок", value: analytics.errors ?? 0 },
      ];

  const handleExport = async () => {
    if (!game?._id) return;

    try {
      const response = await fetch(`${apiUrl}/analytics/${game._id}/export`, {
        headers: {
          Authorization: setAuthToken(),
        },
      });
      if (!response.ok) return;
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${game.title || "analytics"}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className={`app-analytics${embedded ? " app-analytics--embedded" : ""}`}>
      <header className="app-analytics__header">
        <div className="app-analytics__header-main">
          {!embedded ? (
            <h2 className="app-analytics__title">
              {isTrainer ? "Аналитика тренажёра" : "Аналитика сценария"}
            </h2>
          ) : null}
          <Button
            variant="outlined"
            size="small"
            className="app-btn app-btn--secondary app-btn--compact app-analytics__export-btn"
            startIcon={<DownloadOutlinedIcon />}
            onClick={handleExport}
            disabled={sessions.length === 0}
            aria-label="Скачать CSV"
          >
            CSV
          </Button>
        </div>
        {!isTrainer ? (
          <p className="app-analytics__subtitle">
            Подробная статистика прохождений: кто запускал сценарий, как
            двигался по сценам, дошёл ли до финала и где совершал ошибки.
          </p>
        ) : null}
      </header>

      <div className="app-analytics__summary">
        {summary.map((item) => (
          <div className="app-analytics__stat" key={item.label}>
            <span className="app-analytics__stat-label">{item.label}</span>
            <strong className="app-analytics__stat-value">{item.value}</strong>
          </div>
        ))}
      </div>

      <AnalyticsCharts charts={charts} />

      <section className="app-analytics__section">
        <div className="app-analytics__section-head">
          <h3>Детализация прохождений</h3>
        </div>

        <p className="app-analytics__section-desc">
          {isTrainer
            ? "Для каждого участника: число ответов, правильные ответы и точность."
            : "Для каждого игрока: время старта и завершения, маршрут по сценам, факт прохождения до финала и количество ошибочных действий."}
        </p>

        <div className="app-table-wrap app-analytics__table-wrap">
          <table className="app-table app-analytics__table">
            <thead>
              <tr>
                {detailColumns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={detailColumns.length} className="app-table__empty">
                    Данные появятся после подключения сбора статистики в боте
                  </td>
                </tr>
              ) : (
                sessions.map((session, index) => {
                  const totalAnswers = session.totalAnswers ?? 0;
                  const correctCount = session.correctCount ?? 0;
                  const accuracy =
                    totalAnswers > 0
                      ? `${Math.round((correctCount / totalAnswers) * 100)}%`
                      : "—";

                  if (isTrainer) {
                    return (
                      <tr key={session.id ?? index}>
                        <td>{session.displayName ?? session.userId ?? "—"}</td>
                        <td>{formatDateTimeRu(session.startedAt)}</td>
                        <td>
                          {session.completedAt
                            ? formatDateTimeRu(session.completedAt)
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`app-analytics__status app-analytics__status--${
                              session.completed ? "yes" : "no"
                            }`}
                          >
                            {session.completed ? "Да" : "Нет"}
                          </span>
                        </td>
                        <td>{totalAnswers}</td>
                        <td>{correctCount}</td>
                        <td>{accuracy}</td>
                        <td className="app-analytics__path">
                          {session.path?.length
                            ? session.path.join(" → ")
                            : "—"}
                        </td>
                      </tr>
                    );
                  }

                  return (
                  <tr key={session.id ?? index}>
                    <td>{session.displayName ?? session.userId ?? "—"}</td>
                    <td>{formatDateTimeRu(session.startedAt)}</td>
                    <td>
                      {session.completedAt
                        ? formatDateTimeRu(session.completedAt)
                        : "—"}
                    </td>
                    <td>
                      <span
                        className={`app-analytics__status app-analytics__status--${
                          session.completed ? "yes" : "no"
                        }`}
                      >
                        {session.completed ? "Да" : "Нет"}
                      </span>
                    </td>
                    <td>{session.errors ?? 0}</td>
                    <td className="app-analytics__path">
                      {session.path?.length
                        ? session.path.join(" → ")
                        : "—"}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {!isTrainer ? (
      <section className="app-analytics__section app-analytics__section--muted">
        <h3>Что будет доступно дальше</h3>
        <ul className="app-analytics__roadmap">
          <li>История запусков по каждому пользователю Telegram и MAX</li>
          <li>Визуализация пути по сценам и точек выхода из сценария</li>
          <li>Счётчик ошибочных ответов и повторных попыток</li>
          <li>Конверсия: старт → финал, среднее время прохождения</li>
          <li>Экспорт отчёта по прохождениям</li>
        </ul>
      </section>
      ) : null}
    </div>
  );
}
